import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, Printer, FileSpreadsheet } from "lucide-react";

const fmt = (n, dp = 0) => Number.isFinite(+n) ? Number(n).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp }) : "—";

export default function Reports() {
  const { t } = useApp();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [items, setItems] = useState([]);

  const load = async () => {
    const params = {};
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    const { data } = await api.get("/transactions", { params: { ...params, limit: 5000 } });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to]);

  const totalProfit = items.reduce((s, t) => s + (t.total_profit_idr || 0), 0);
  const completedCount = items.filter((i) => i.status === "completed").length;

  const csv = async () => {
    const token = localStorage.getItem("fx_token");
    const params = new URLSearchParams();
    if (from) params.set("date_from", from);
    if (to) params.set("date_to", to);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/csv?${params}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `transactions_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{t("reports")}</h1>

      <Card className="p-4 glass border-slate-800 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">From</Label>
          <Input data-testid="rep-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 w-44" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input data-testid="rep-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 w-44" />
        </div>
        <div className="flex gap-2 ml-auto">
          <Button data-testid="rep-csv" onClick={csv} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> {t("export_csv")}
          </Button>
          <Button data-testid="rep-print" onClick={() => window.print()} variant="outline" className="border-slate-700">
            <Printer className="w-4 h-4 mr-2" /> Print / PDF
          </Button>
        </div>
      </Card>

      <div id="print-receipt" className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-5 glass border-slate-800">
            <div className="text-xs text-slate-400 uppercase tracking-wider">{t("total")} Transactions</div>
            <div className="font-mono text-2xl font-bold mt-2">{items.length}</div>
            <div className="text-xs text-slate-500 mt-1">{completedCount} completed</div>
          </Card>
          <Card className="p-5 glass border-slate-800">
            <div className="text-xs text-slate-400 uppercase tracking-wider">{t("total_profit")}</div>
            <div className="font-mono text-2xl font-bold mt-2 text-emerald-400">Rp {fmt(totalProfit)}</div>
          </Card>
          <Card className="p-5 glass border-slate-800">
            <div className="text-xs text-slate-400 uppercase tracking-wider">Period</div>
            <div className="text-sm mt-2">{from || "—"} → {to || "today"}</div>
          </Card>
        </div>

        <Card className="glass border-slate-800 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-800 text-left text-xs uppercase text-slate-400">
              <tr>
                <th className="p-3">TX #</th>
                <th className="p-3">Date</th>
                <th className="p-3">Customer</th>
                <th className="p-3">Chain</th>
                <th className="p-3 text-right">Profit (IDR)</th>
                <th className="p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((tx) => (
                <tr key={tx.id} className="border-b border-slate-800/50">
                  <td className="p-3 font-mono text-xs text-amber-400">{tx.tx_number}</td>
                  <td className="p-3 text-xs text-slate-400">{new Date(tx.created_at).toLocaleDateString()}</td>
                  <td className="p-3">{tx.customer_name || "Walk-in"}</td>
                  <td className="p-3 text-xs">{tx.legs?.map((l) => l.from_currency).join("→")}{tx.legs?.length ? "→" + tx.legs[tx.legs.length - 1].to_currency : ""}</td>
                  <td className={`p-3 text-right font-mono ${tx.total_profit_idr >= 0 ? "text-emerald-400" : "text-red-400"}`}>{fmt(tx.total_profit_idr)}</td>
                  <td className="p-3 text-xs">{tx.status}</td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-slate-500">No transactions in range</td></tr>
              )}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}
